import React, { useState, useEffect, useRef } from 'react';
import { Search, User, Building2, X } from 'lucide-react';
import { Input } from './ui/input';
import { Button } from './ui/button';
import { sortClientsAlpha } from '../utils/clientSort';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * ClientPicker — autocomplete that pulls from /api/clients/directory.
 * On select, calls `onSelect(clientRecord)` with the full client dict so
 * the parent form can populate every field. Records are aggregated from
 * past estimates + saved clients, so as soon as you create one estimate
 * for "John Smith", he'll show up here next time.
 */
export default function ClientPicker({ onSelect, currentName = '', placeholder = 'Search saved clients…' }) {
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [allClients, setAllClients] = useState(null);  // null = not yet fetched
  const containerRef = useRef(null);

  // Close dropdown on outside click
  useEffect(() => {
    const onClick = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, []);

  // Lazy-load directory once the user opens the picker
  const ensureLoaded = async () => {
    if (allClients !== null) return;
    setLoading(true);
    try {
      const res = await fetch(`${API}/clients/directory`, { credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        const sorted = sortClientsAlpha(data);
        setAllClients(sorted);
        setResults(sorted.slice(0, 12));
      } else {
        setAllClients([]);
      }
    } catch {
      setAllClients([]);
    } finally {
      setLoading(false);
    }
  };

  const handleFocus = async () => {
    setOpen(true);
    await ensureLoaded();
  };

  const handleQueryChange = (v) => {
    setQuery(v);
    if (!allClients) return;
    if (!v.trim()) {
      setResults(allClients.slice(0, 12));
      return;
    }
    const q = v.toLowerCase();
    setResults(
      allClients
        .filter(c =>
          (c.name || '').toLowerCase().includes(q)
          || (c.company || '').toLowerCase().includes(q)
          || (c.email || '').toLowerCase().includes(q)
          || (c.phone || '').toLowerCase().includes(q)
        )
        .slice(0, 20)
    );
  };

  const handlePick = (c) => {
    onSelect?.(c);
    setOpen(false);
    setQuery('');
  };

  return (
    <div ref={containerRef} className="relative" data-testid="client-picker">
      <div className="flex items-center gap-2">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#6B7A73]" />
          <Input
            value={query}
            onChange={e => handleQueryChange(e.target.value)}
            onFocus={handleFocus}
            placeholder={placeholder}
            className="pl-9 bg-white border-[#E6EBE8] rounded-md text-sm"
            data-testid="client-picker-input"
          />
          {query && (
            <button
              type="button"
              onClick={() => { setQuery(''); handleQueryChange(''); }}
              className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-[#6B7A73] hover:text-[#1A1D1C]"
            >
              <X className="w-3.5 h-3.5" />
            </button>
          )}
        </div>
      </div>

      {open && (
        <div className="absolute z-30 mt-1 w-full bg-white border border-[#E6EBE8] rounded-md shadow-lg max-h-80 overflow-y-auto">
          {loading ? (
            <div className="p-4 text-center text-xs text-[#6B7A73]">Loading…</div>
          ) : (results.length === 0) ? (
            <div className="p-4 text-center text-xs text-[#6B7A73]">
              {query ? 'No matches — keep typing in the form below to add a new client.'
                     : 'No saved clients yet — your first one will be auto-saved when you complete an estimate.'}
            </div>
          ) : (
            <>
              {currentName && (
                <div className="px-3 py-2 text-[10px] uppercase tracking-wider text-[#6B7A73] border-b border-[#E6EBE8] bg-[#FAFAF8]">
                  Currently: {currentName}
                </div>
              )}
              <ul>
                {results.map((c, i) => {
                  const display = c.company || c.name || c.email || 'Unnamed';
                  const sub = [c.name && c.company ? c.name : '', c.email, c.phone, c.address1].filter(Boolean).join(' · ');
                  return (
                    <li key={`${c.email || c.name || i}-${i}`}>
                      <button
                        type="button"
                        onClick={() => handlePick(c)}
                        className="w-full text-left px-3 py-2 hover:bg-[#F0F3F1] flex items-start gap-2 transition-colors"
                        data-testid={`client-picker-option-${i}`}
                      >
                        {c.company ? (
                          <Building2 className="w-3.5 h-3.5 text-[#2D6B68] mt-0.5 flex-shrink-0" />
                        ) : (
                          <User className="w-3.5 h-3.5 text-[#6B7A73] mt-0.5 flex-shrink-0" />
                        )}
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-[#1A1D1C] truncate">{display}</p>
                          {sub && <p className="text-[10px] text-[#6B7A73] truncate mt-0.5">{sub}</p>}
                        </div>
                        {c.estimate_count > 0 && (
                          <span className="text-[10px] text-[#6B7A73] bg-[#F5F6F5] px-1.5 py-0.5 rounded-full flex-shrink-0">
                            {c.estimate_count} est{c.estimate_count !== 1 ? 's' : ''}
                          </span>
                        )}
                      </button>
                    </li>
                  );
                })}
              </ul>
            </>
          )}
          <div className="px-3 py-2 border-t border-[#E6EBE8] bg-[#FAFAF8] text-[10px] text-[#6B7A73]">
            Type to filter, or close this and fill the form below for a new client.
          </div>
        </div>
      )}
    </div>
  );
}
